import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { countQueuedStationScans, type QueuedStation } from '../helperStations/localStation';
import { useConnectivity } from '../lib/connectivity';
import { onBackgroundSendDone, sendRegister, type BackgroundOutcome } from '../pos/backgroundSend';
import { countUnsentByRegister, lastPosEvents, type UnsentRegister } from '../pos/sessions';
import { CASH_PAGE } from './workPages';

/** Solange etwas wartet, wird alle 30 s neu gezählt. */
export const RECOUNT_MS = 30_000;
/** So lange bleibt „Wieder online · n gesendet“ stehen. */
export const RECOVERED_MS = 3_000;

interface Unsent {
  registers: UnsentRegister[];
  stations: QueuedStation[];
}

const NONE: Unsent = { registers: [], stations: [] };

const total = (unsent: Unsent): number =>
  unsent.registers.reduce((sum, entry) => sum + entry.count, 0) +
  unsent.stations.reduce((sum, entry) => sum + entry.count, 0);

const sales = (n: number): string => (n === 1 ? '1 Vorgang' : `${String(n)} Vorgänge`);

/** Rückmeldung zu „Jetzt senden“: Auch „nichts passiert“ bekommt einen lesbaren Grund. */
const SEND_RESULT: Record<BackgroundOutcome, string> = {
  sent: 'Gesendet.',
  partial: 'Teilweise gesendet, der Rest folgt automatisch.',
  nothing: 'Nichts mehr zu senden.',
  busy: 'Die Kasse ist gerade geöffnet und sendet selbst.',
  revoked: 'Nicht gesendet: Die Kasse ist abgekoppelt. Bitte die Kasse öffnen.',
  failed: 'Nicht gesendet: Der Server ist nicht erreichbar. Es wird automatisch wiederholt.',
  unsupported: 'Dieses Gerät sendet nur aus der geöffneten Kasse. Bitte die Kasse öffnen.',
  skipped: 'Bitte die Kasse öffnen: Sie meldet sich neu an und sendet dann.',
};

/**
 * Einheitliche Anzeige „Offline · N nicht gesendet“ (U15a) auf allen Seiten außer der Kasse (dort zeigt die Kopfzeile den
 * Stand). N sind die wartenden Kassenvorgänge dieses Geräts plus die wartenden Rückgabe-Scans der Stationen. Antippen öffnet
 * die Liste je Kasse/Station mit „Kasse öffnen“ und „Jetzt senden“.
 */
export function ConnectivityBar() {
  const location = useLocation();
  const state = useConnectivity();
  const [unsent, setUnsent] = useState<Unsent>(NONE);
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState<string | null>(null);
  const [sendResult, setSendResult] = useState<string | null>(null);
  const [recovered, setRecovered] = useState<number | null>(null);
  const peak = useRef(0);
  const previous = useRef(state);
  const hidden = CASH_PAGE.test(location.pathname);

  const [tick, setTick] = useState(0);
  const recount = useCallback((): void => {
    setTick((n) => n + 1);
  }, []);

  // Neu zählen: beim Start, bei jedem Zustandswechsel, nach jedem Hintergrund-Lauf und alle 30 s, solange etwas wartet
  useEffect(() => {
    const guard = { cancelled: false };
    void (async () => {
      const [registers, stations] = await Promise.all([
        countUnsentByRegister().catch(() => []),
        countQueuedStationScans(),
      ]);
      if (guard.cancelled) return;
      setUnsent({ registers: registers.filter((entry) => entry.count > 0), stations });
    })();
    return () => {
      guard.cancelled = true;
    };
  }, [state, tick]);
  const count = total(unsent);
  useEffect(() => {
    if (count === 0) return;
    const timer = setInterval(recount, RECOUNT_MS);
    return () => {
      clearInterval(timer);
    };
  }, [count, recount]);
  useEffect(() => onBackgroundSendDone(recount), [recount]);

  // „Wieder online · n gesendet ✓“ nach der Rückkehr, für 3 s
  useEffect(() => {
    if (state !== 'online') peak.current = Math.max(peak.current, count);
  }, [state, count]);
  useEffect(() => {
    const was = previous.current;
    previous.current = state;
    if (state === 'online' && was !== 'online' && peak.current > 0) {
      setRecovered(peak.current);
      peak.current = 0;
    }
  }, [state]);
  useEffect(() => {
    if (recovered === null) return;
    const timer = setTimeout(() => {
      setRecovered(null);
    }, RECOVERED_MS);
    return () => {
      clearTimeout(timer);
    };
    // Mit jeder Änderung der Zahl neu: „gesendet ✓“ steht 3 s nach dem Ende des Sendens, nicht nach der Rückkehr
  }, [recovered, count]);

  const sendNow = async (eventId: string): Promise<void> => {
    setSending(eventId);
    setSendResult(null);
    try {
      setSendResult(SEND_RESULT[await sendRegister(eventId)]);
    } catch {
      setSendResult(SEND_RESULT.failed);
    } finally {
      setSending(null);
      recount();
    }
  };

  const online = state === 'online';
  const showRecovered = online && count === 0 && recovered !== null;
  // Vorgelesen wird nur der Wechsel des Zustands und das Ergebnis von „Jetzt senden“, nicht jede neue Zahl (kein Dauerreden
  // an der Rückgabe-Station, die mit jedem Scan zählt). Die Region bleibt immer im Dokument, damit Wechsel ankommen.
  const announce = hidden
    ? ''
    : (sendResult ??
      (showRecovered
        ? `Verbindung wieder da, ${sales(recovered)} gesendet`
        : state === 'offline'
          ? 'Verbindung: offline'
          : state === 'weak'
            ? 'Verbindung: schwaches Netz'
            : ''));
  const live = (
    <p className="visually-hidden" role="status">
      {announce}
    </p>
  );
  if (hidden || (online && count === 0 && !showRecovered)) return live;

  const titles = new Map(lastPosEvents().map((entry) => [entry.eventId, entry]));
  let text: string;
  let tone: 'offline' | 'weak' | 'ok' | 'pending';
  if (showRecovered) {
    text = `Wieder online · ${sales(recovered)} gesendet ✓`;
    tone = 'ok';
  } else if (state === 'offline') {
    text = count === 0 ? 'Offline' : `Offline · ${String(count)} nicht gesendet`;
    tone = 'offline';
  } else if (state === 'weak') {
    text = count === 0 ? 'Schwaches Netz' : `Schwaches Netz · ${String(count)} nicht gesendet`;
    tone = 'weak';
  } else {
    text = `${String(count)} nicht gesendet`;
    tone = 'pending';
  }

  return (
    <div className={`connectivity-bar connectivity-bar--${tone}`}>
      {live}
      <div className="connectivity-bar__row">
        <p className="connectivity-bar__text">{text}</p>
        {count > 0 && (
          <button
            type="button"
            className="connectivity-bar__more"
            aria-expanded={open}
            aria-controls="connectivity-details"
            onClick={() => {
              setOpen((value) => !value);
            }}
          >
            {open ? 'Weniger' : 'Details'}
          </button>
        )}
      </div>
      {sendResult !== null && (
        <p className="connectivity-bar__result" aria-hidden="true">
          {sendResult}
        </p>
      )}
      {open && count > 0 && (
        <ul id="connectivity-details" className="connectivity-bar__list">
          {unsent.registers.map((entry) => {
            const known = titles.get(entry.eventId);
            const name = known?.title ?? 'Kasse';
            const to =
              known?.device === true
                ? `/kasse-geraet/${entry.eventId}`
                : `/basare/${entry.eventId}/kasse`;
            return (
              <li key={`k-${entry.eventId}`}>
                <span>
                  {name}: {sales(entry.count)} nicht gesendet
                </span>
                <span className="connectivity-bar__actions">
                  <Link className="btn btn--small" to={to}>
                    Kasse öffnen
                  </Link>
                  <button
                    type="button"
                    className="btn btn--small"
                    disabled={sending !== null || state === 'offline'}
                    onClick={() => {
                      void sendNow(entry.eventId);
                    }}
                  >
                    {sending === entry.eventId ? 'Sendet …' : 'Jetzt senden'}
                  </button>
                </span>
              </li>
            );
          })}
          {unsent.stations.map((entry) => (
            <li key={`s-${entry.eventId}`}>
              <span>
                Rückgabe: {entry.count === 1 ? '1 Scan' : `${String(entry.count)} Scans`} nicht
                gesendet
              </span>
              <span className="connectivity-bar__actions">
                <Link className="btn btn--small" to={`/basare/${entry.eventId}/rueckgabe`}>
                  Station öffnen
                </Link>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
